/**
 * useLlmEditorContext — lädt im Bewertungs-Editor den Pseudonymisierungs-
 * Kontext (Client-Stammdaten + Jobcenter-Felder) für die LLM-Calls.
 *
 * Demo-Modus: nimmt die Demo-Fixture (Maria Mustermann, München).
 * Live-Modus: lädt CaseRow aus der DB und baut Client + Case zusammen.
 *
 * Wird im AssessmentEditor pro Case einmal gemountet — der zurückgegebene
 * PseudonymizationContext fließt in jeden LlmSuggestionButton.
 *
 * Failsafe: bei Fehler (Case nicht gefunden, RLS-Block) bleibt der Hook
 * im `loading=false, ctx=null`-State, der Button rendert dann gar nicht.
 */

import { useEffect, useState } from 'react';
import type { statement } from '@tragfaehigkeit/core';

import type { CaseRow } from '../supabaseTypes';
import { getCase } from '../supabaseCases';
import { buildDemoStatementInput } from '../statement/demoFixture';
import { useAuthOrSession } from '../authOrSession';
import { buildPseudonymizationContextFromCase } from './buildPseudonymizationContext';
import type { PseudonymizationContext } from './pseudonymize';

type ClientStaticData = statement.ClientStaticData;

// Re-export für saubere imports im Editor
export type { PseudonymizationContext };

interface UseLlmEditorContextResult {
  readonly pseudonymizationContext: PseudonymizationContext | null;
  readonly isLoading: boolean;
  /** Audit-relevante caseId für den LLM-Call. null im Demo-Modus. */
  readonly caseIdForLog: string | null;
}

/**
 * Pure helper: leitet ClientStaticData aus einer CaseRow ab.
 * Exportiert für Tests.
 */
export function deriveClientFromCaseRow(row: CaseRow): ClientStaticData | null {
  const fullName = row.client_full_name?.trim();
  if (!fullName) return null;

  const firstName = fullName.split(/\s+/)[0];
  const client: ClientStaticData = {
    fullName,
    ...(firstName ? { firstName } : {}),
    ...(row.client_address ? { address: row.client_address } : {}),
    ...(row.client_birthdate ? { birthDate: new Date(row.client_birthdate) } : {}),
  };
  return client;
}

export function useLlmEditorContext(caseId: string): UseLlmEditorContextResult {
  const { isMock } = useAuthOrSession();
  const [ctx, setCtx] = useState<PseudonymizationContext | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    // Demo-Pfad: keine DB-Calls, nutze Fixture.
    if (isMock || caseId === 'demo') {
      const demo = buildDemoStatementInput();
      const context = buildPseudonymizationContextFromCase({
        client: demo.client,
        caseRow: null, // Demo hat kein Jobcenter-Feld in der DB
      });
      setCtx(context);
      setIsLoading(false);
      return;
    }

    // Live-Pfad: lade CaseRow + leite Client ab
    setIsLoading(true);
    void getCase(caseId)
      .then((row) => {
        if (cancelled) return;
        if (!row) {
          setCtx(null);
          setIsLoading(false);
          return;
        }
        const client = deriveClientFromCaseRow(row);
        if (!client) {
          // Kein Klarname → kein Pseudonymisierungs-Kontext sinnvoll.
          // Button rendert dann nicht (Aufrufer prüft ctx).
          setCtx(null);
          setIsLoading(false);
          return;
        }
        const context = buildPseudonymizationContextFromCase({
          client,
          caseRow: row,
        });
        setCtx(context);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn('useLlmEditorContext: getCase failed', err);
        setCtx(null);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [caseId, isMock]);

  const caseIdForLog = isMock || caseId === 'demo' ? null : caseId;

  return {
    pseudonymizationContext: ctx,
    isLoading,
    caseIdForLog,
  };
}
